import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useCallback } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { CommentsGroupMemberBinding, CommentsOpenRequestProvider } from '../../comments';
import type { UrlTileHost } from '../../url-tiles';
import { useJobItemDetails } from '../api/use-job-item-details';
import { JobArchitecturePermissionsContextProvider } from '../context/JobArchitecturePermissionsContext';
import {
    JobProfileCommentsSurfaceProvider,
    useJobProfileCommentsSubject,
} from '../job-profile-section/comments/JobProfileCommentsSurfaceProvider';
import { useJobProfileCommentsApi } from '../job-profile-section/comments/job-profile-comments-api';
import { Details as JobProfileDetails } from '../job-profile-section/Details';
import { JobArchitectureScopeProvider } from '../url-navigation/job-architecture-scope';
import type { JsaProfileDetailTilePayload } from '../url-tile-payloads';
import { useJobArchitectureUrlTileActions } from './use-job-architecture-url-tile-actions';
import { useJobProfileTileLanguage } from './use-job-profile-tile-language';

type JobProfileTileProps = {
    readonly close: () => void;
    readonly host?: UrlTileHost;
    readonly payload: JsaProfileDetailTilePayload;
};

export const JobProfileTileProviderStack: FunctionComponent<PropsWithChildren<{ readonly architectureId: string }>> = ({
    architectureId,
    children,
}) => (
    <JobArchitectureScopeProvider architectureId={architectureId}>
        <JobArchitecturePermissionsContextProvider>{children}</JobArchitecturePermissionsContextProvider>
    </JobArchitectureScopeProvider>
);

/**
 * The profile's own title, for the source chip the group comment tile shows on each card.
 *
 * Shares the details query `Details.tsx` already runs for this tile, so this costs nothing; the
 * generic entity noun stays as the fallback while the query is in flight or the active language
 * has no title yet.
 */
const useJobProfileMemberLabel = (payload: JsaProfileDetailTilePayload, activeLanguage: LanguageCodeForJSA): string => {
    const translate = useTranslate();
    const { data } = useJobItemDetails({
        architectureId: payload.architectureId,
        itemId: payload.profileId,
        itemType: payload.itemType,
        version: is.number(payload.version) ? payload.version.toString() : undefined,
    });
    const title = data?.jobProfile?.translations.find(({ language }) => language === activeLanguage)?.title;

    return is.nonEmptyString(title)
        ? title
        : translate(payload.itemType === 'job-level' ? 'overwrites.jobLevel.one' : 'overwrites.jobRole.one');
};

const JobProfileTileSurface: FunctionComponent<JobProfileTileProps> = ({ close, host, payload }) => {
    const { toggleJobProfileTileCommentsOpen, updateJobProfileTileCommentsOpen } = useJobArchitectureUrlTileActions();
    const { activeLanguage } = useJobProfileTileLanguage(payload);
    const memberLabel = useJobProfileMemberLabel(payload, activeLanguage);
    const commentsApi = useJobProfileCommentsApi();
    const commentsSubject = useJobProfileCommentsSubject({
        architectureId: payload.architectureId,
        itemId: payload.profileId,
        itemType: payload.itemType,
        language: activeLanguage,
        requestedVersion: payload.version,
    });
    const requestCommentsOpen = useCallback(
        () => updateJobProfileTileCommentsOpen(true),
        [updateJobProfileTileCommentsOpen],
    );

    return (
        <JobProfileCommentsSurfaceProvider
            architectureId={payload.architectureId}
            itemId={payload.profileId}
            itemType={payload.itemType}
            language={activeLanguage}
            version={payload.version}
        >
            <CommentsOpenRequestProvider requestCommentsOpen={requestCommentsOpen}>
                <CommentsGroupMemberBinding
                    api={commentsApi}
                    label={memberLabel}
                    memberRole="parent"
                    subject={commentsSubject}
                />
                <JobProfileDetails
                    architectureId={payload.architectureId}
                    host={host}
                    isArchived={payload.isArchived}
                    isCommentsOpen={payload.isCommentsOpen === true}
                    isOpen={true}
                    itemId={payload.profileId}
                    itemType={payload.itemType}
                    mode={payload.mode}
                    preferredLanguage={payload.preferredLanguage}
                    version={payload.version}
                    onClose={host === 'main' ? undefined : close}
                    onToggleComments={toggleJobProfileTileCommentsOpen}
                />
            </CommentsOpenRequestProvider>
        </JobProfileCommentsSurfaceProvider>
    );
};

export const JobProfileTile: FunctionComponent<JobProfileTileProps> = ({ close, host, payload }) => (
    <JobProfileTileProviderStack architectureId={payload.architectureId}>
        <JobProfileTileSurface close={close} host={host} payload={payload} />
    </JobProfileTileProviderStack>
);
